// src/components/lead/FullLeadView.tsx
import type { LeadBundle } from "../../hooks/useCurrentLead";
import type { DerivedBundle } from "../../hooks/useLeadDerivedFields";
import type { Lead } from "@/types/lead";
import { ChecklistCard } from "./sections/ChecklistCard";
import { ContactCard } from "./sections/ContactCard";
import { CustomerCalendarCard } from "./sections/CustomerCalendarCard";
import { LeadWorkplaceRemindersSection } from "./sections/LeadWorkplaceRemindersSection";
import { LocationSourceCard } from "./sections/LocationSourceCard";
import { ProductCard } from "./sections/ProductCard";
import { useChecklistFieldFocus } from "./sections/useChecklistFieldFocus";

type Props = {
  lead: LeadBundle;
  derived: DerivedBundle;
  currentLead: Lead;
  onOpenSalesConsole?: () => void;
  createFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
  onLinkedOpportunity: (opportunityId: string) => void;
  onViewOpportunity: (opportunityId: string) => void;
  setToast: (t: { message: string; type: "success" | "error" } | null) => void;
};

export function FullLeadView(props: Props) {
  const {
    lead,
    derived,
    currentLead,
    onOpenSalesConsole,
    createFollowUp,
    onLinkedOpportunity,
    onViewOpportunity,
    setToast,
  } = props;

	// Slice B1: clicking a missing-field checklist row opens the right
	// accordion (Product or Location & Source) if closed, then scrolls
	// the underlying input into view and focuses it.
	const focusChecklistField = useChecklistFieldFocus({
		showProductDetails: lead.showProductDetails,
		setShowProductDetails: lead.setShowProductDetails,
		showLocationDetails: lead.showLocationDetails,
		setShowLocationDetails: lead.setShowLocationDetails,
	});

	return (
		<div data-app-panel="FullLeadView" className="grid grid-cols-1 lg:grid-cols-2 gap-4"> {/* Two-column card layout */}
			<div className="space-y-4 min-w-0"> {/* Left column: Contact + Product */}
				<ContactCard
					lead={currentLead}
					e={lead.e}
					phoneDraft={lead.phoneDraft}
					setPhoneDraft={lead.setPhoneDraft}
					normalizePhoneOrEmpty={lead.normalizePhoneOrEmpty}
					updateCurrentLeadField={lead.updateCurrentLeadField}
					setToast={setToast}
				/>

				{/* Product card */}
				<ProductCard
					lead={currentLead}
					e={lead.e}
					showProductDetails={lead.showProductDetails}
					setShowProductDetails={lead.setShowProductDetails}
					updateCurrentLeadField={lead.updateCurrentLeadField}
					hasTranscript={Boolean(currentLead.transcript?.trim())}
					onLinkedOpportunity={onLinkedOpportunity}
					onViewOpportunity={onViewOpportunity}
					setToast={setToast}
				/>
			</div>

			<div className="space-y-4 min-w-0"> {/* Right column: Location & Source + Checklist */}
				<LocationSourceCard
					e={lead.e}
					locationDisplay={derived.locationDisplay}
					leadSourceDisplay={derived.leadSourceDisplay}
					timelineDisplay={derived.timelineDisplay}
					installAddressDisplay={derived.installAddressDisplay}
					showLocationDetails={lead.showLocationDetails}
					setShowLocationDetails={lead.setShowLocationDetails}
					updateCurrentLeadField={lead.updateCurrentLeadField}
				/>

				<ChecklistCard e={lead.e} onItemFocus={focusChecklistField} />
				<LeadWorkplaceRemindersSection leadId={currentLead.id} />
				<CustomerCalendarCard lead={currentLead} onOpenSalesConsole={onOpenSalesConsole} createFollowUp={createFollowUp} />
			</div>
		</div>
	);
}

